<script setup lang="ts">
// Domain-free chip/tag entry (ORP-19871; ledger B22, CM-30-07, and the ChipInput HALF of CM-22-07
// — that row's `FileDropzone` ask is a different component and is NOT touched here).
//
// FOUR legacy call sites hand-rolled this control and no two agreed:
//   * `UserPermissionModal.vue:392–455` (22, ORP-18889) — emails. Commits on Enter|Tab|,|; ,
//     splits paste on `[,;\n\t\s]+`, backspace pops the last chip, and tints each chip one of four
//     states (valid / self / already-invited / bad format). ADMITS a bad address, then marks it.
//   * `NotificationUpdateModal.vue:22–49` (22, ORP-18886) — WhatsApp numbers. A separate "+"
//     button, chips rendered through a FORMATTER (`formatTRGsm3322`) so the displayed text ≠ the
//     stored value, and the entry REFUSED before it can become a chip. The strict inverse of the
//     screen above, which is why admit-and-mark and refuse-before-add are separate props.
//   * `CustomArea.vue` (30, CM-30-07 — 200 ln, 11 call sites across 5 features) — keywords,
//     source/medium pairs, template tags, workspace ids, URL patterns. Enter plus an inline "Add"
//     pill, splits paste on `[,\n\r]+` ONLY when it yields >1 value, enforces `max`, folds case.
//   * `SchedulesQueriesModal` (allowed emails) — emails again, through CustomArea.
//
// They differ on five real axes, so those became props rather than three components:
//   * `delimiters` — permissions split on WHITESPACE, keywords must NOT: "running shoes" is one
//     keyword and `/accounts/*/workspaces/*` is one URL pattern. No single pattern serves both,
//     so the caller names its separators (and paste reuses them — see `tokenize.ts`).
//   * `normalize` — all of them lower-case, but case folding is a DOMAIN decision; the default
//     only trims. De-duplication is case-insensitive REGARDLESS, so a caller never has to destroy
//     the user's capitalisation to stop "Nike"/"nike" both landing.
//   * `validate` vs `chipTone` — the two validation models legacy actually uses (see the first two
//     call sites). Neither rule lives here: 02 knows nothing about emails, phones or keywords.
//   * `addOnBlur` — how the half-typed entry survives "Save" (see legacy defect 5 below).
//   * `addLabel` — presence of a caller-localised label renders CustomArea's inline add control.
//
// Five legacy behaviours are deliberately NOT ported, because each is a defect:
//   1. Tab ALWAYS called `preventDefault()`, so a keyboard user could never leave the field. Tab
//      here commits only when there IS pending text, and otherwise moves focus.
//   2. Paste unconditionally cleared the field, destroying text the user had already typed.
//   3. Paste always intercepted, so a single pasted value could not be corrected before it became
//      a chip. A one-token paste now falls through to the native handler (CustomArea's rule).
//   4. Removing a chip unmounted the focused button and stranded focus on `<body>`; removal now
//      returns focus to the field.
//   5. `CustomArea` emitted `last-values` so callers could push the un-committed entry on save —
//      but every consumer guarded it with `!list.length > 2`, which parses as `(!list.length) > 2`
//      and is ALWAYS false, so the entry was never salvaged (`BrandedArea.vue:65`,
//      `SchedulesQueriesModal.vue:183`). `addOnBlur` implements the intent: pressing Save blurs
//      the field, which commits. No pending-value emit is exposed, because that seam only ever
//      existed to work around this.
//
// The chip VISUAL is the shared `Badge` primitive, so chips have one owner and `UiTone` means the
// same thing here as everywhere else. What is NOT delegated is `Badge.removable`: it names its ×
// with a single `aria-label`, which would announce every remove control in a 20-chip field
// identically. Instead the × is rendered into Badge's slot and named by `aria-labelledby` pointing
// at BOTH the reserved-label span and the chip's own text, so AT reads "Clear ada@orphex.co"
// without gluing two localised fragments together in an assumed word order.
//
// NOT in scope: the option/typeahead dropdown `CustomArea` also carries. That is `MultiSelect`
// (a searchable Combobox), duplicating it would fork the floating-layer seam, and its
// `options`/`fields`/`keyField` props have ZERO production call sites today.
import { computed, ref, useId } from 'vue'

import { splitEntries } from './tokenize'
import type { ChipInputExpose, ChipInputProps, ChipReject, ChipRejectReason } from './types'

import { injectUiLabels } from '@/shared/i18n'
import { Badge } from '@/shared/ui/badge'
import { Icon } from '@/shared/ui/icon'
import type { UiTone } from '@/shared/ui/types'

const model = defineModel<string[]>({ default: () => [] })

const props = withDefaults(defineProps<ChipInputProps>(), {
  size: 'md',
  hideLabel: false,
  disabled: false,
  error: false,
  addOnBlur: false,
  delimiters: () => [',', ';'],
})

const emit = defineEmits<{ reject: [rejection: ChipReject] }>()

// Reserved labels resolve through the injected `ui.*` bag (contract 04 §9), so the primitive
// renders localised copy without importing vue-i18n. Callers may still override per prop.
const uiLabels = injectUiLabels()
const resolvedRemoveLabel = computed(() => props.removeLabel ?? uiLabels.remove)

const baseId = useId()
const labelId = `${baseId}-label`
const inputId = `${baseId}-field`
const removeLabelId = `${baseId}-remove`
const chipTextId = (index: number): string => `${baseId}-chip-${index}`

const pending = ref('')
const field = ref<HTMLInputElement | null>(null)

function normalizeEntry(raw: string): string {
  return props.normalize ? props.normalize(raw) : raw.trim()
}

function toneOf(value: string): UiTone {
  return props.chipTone?.(value) ?? 'neutral'
}

function refuse(value: string, reason: ChipRejectReason): void {
  emit('reject', { value, reason })
}

/**
 * Apply a batch of raw entries as ONE model write, and report whether anything was admitted.
 *
 * Batching is not an optimisation. Under a CONTROLLED `v-model` the model ref does not reflect a
 * write until the parent round-trips, so writing per entry would make each value in a pasted list
 * overwrite the previous one and only the last would survive.
 */
function admit(raws: readonly string[]): boolean {
  const next = [...model.value]
  // De-duplication is CASE-INSENSITIVE (every legacy screen folded case) but the stored value
  // keeps the user's capitalisation — `CustomArea` had to lower-case what it stored to get this,
  // which quietly turned "Nike" into "nike" in a branded-term list.
  const seen = new Set(next.map((value) => value.toLowerCase()))
  for (const raw of raws) {
    const value = normalizeEntry(raw)
    if (!value) continue
    if (seen.has(value.toLowerCase())) refuse(value, 'duplicate')
    else if (props.validate && !props.validate(value)) refuse(value, 'invalid')
    else if (props.max !== undefined && next.length >= props.max) refuse(value, 'max')
    else {
      next.push(value)
      seen.add(value.toLowerCase())
    }
  }

  if (next.length === model.value.length) return false
  model.value = next
  return true
}

function commitPending(): void {
  // A refused entry STAYS in the field so the user can correct it. Legacy cleared it blindly,
  // which silently ate a mistyped address.
  if (admit([pending.value])) pending.value = ''
}

function focus(): void {
  field.value?.focus()
}

function removeAt(index: number): void {
  model.value = model.value.filter((_, position) => position !== index)
  // The button that was just clicked has unmounted — put focus somewhere navigable.
  focus()
}

/** The inline add control (`CustomArea`'s "Add" pill / Notifications' "+"), which never blurs. */
function commitFromButton(): void {
  commitPending()
  focus()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || props.delimiters.includes(event.key)) {
    event.preventDefault()
    commitPending()
    return
  }
  if (event.key === 'Tab') {
    // Swallow Tab ONLY when there is something to commit (legacy defect 1).
    if (!pending.value.trim()) return
    event.preventDefault()
    commitPending()
    return
  }
  if (event.key === 'Backspace' && pending.value === '' && model.value.length > 0) {
    event.preventDefault()
    model.value = model.value.slice(0, -1)
  }
}

function onPaste(event: ClipboardEvent): void {
  const entries = splitEntries(event.clipboardData?.getData('text') ?? '', props.delimiters)
  // One token → let the browser paste into the field, caret and all, so it can be edited before
  // committing. Only a MULTI-value paste is a chip operation (legacy defects 2 and 3).
  if (entries.length < 2) return
  event.preventDefault()
  admit(entries)
}

function onBlur(): void {
  if (props.addOnBlur) commitPending()
}

defineExpose<ChipInputExpose>({ focus })
</script>

<template>
  <div
    class="ui-chip-input relative w-full font-sans"
    :class="[
      `ui-chip-input--${size}`,
      { 'ui-chip-input--error': error, 'ui-chip-input--disabled': disabled },
    ]"
  >
    <label
      :id="labelId"
      :for="inputId"
      class="ui-chip-input__label absolute -top-2 left-2 z-10 px-1 text-xs font-medium text-muted bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] rounded-sm"
      :class="{ 'ui-chip-input__label--sr-only sr-only': hideLabel }"
    >
      {{ label }}
    </label>

    <div
      class="ui-chip-input__control flex flex-wrap items-center gap-[0.3rem] py-1 px-[0.4rem] rounded-sm bg-surface border shadow-input"
      :class="[
        size === 'xs' || size === 'sm'
          ? 'min-h-8'
          : size === 'lg' || size === 'xl'
            ? 'min-h-12'
            : 'min-h-10',
        error
          ? 'border-danger focus-within:shadow-[0_0_0_1px_var(--ui-color-danger)]'
          : 'border-border focus-within:border-ring focus-within:shadow-[0_0_0_1px_var(--ui-color-ring)]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text',
      ]"
      @click="focus"
    >
      <!-- `display: contents` keeps chips in the control's wrap flow; the explicit role keeps the
           list semantics that `display: contents` otherwise drops in some engines. -->
      <ul
        v-if="model.length"
        class="ui-chip-input__chips contents m-0 p-0 list-none"
        role="list"
        :aria-labelledby="labelId"
      >
        <li
          v-for="(value, index) in model"
          :key="`${index}-${value}`"
          class="inline-flex max-w-full"
        >
          <Badge class="ui-chip-input__chip max-w-full" :tone="toneOf(value)" size="sm">
            <span
              :id="chipTextId(index)"
              class="ui-chip-input__chip-text overflow-hidden text-ellipsis whitespace-nowrap"
            >
              <slot name="chip" :value="value" :index="index">{{ value }}</slot>
            </span>
            <button
              v-if="!disabled"
              type="button"
              class="ui-chip-input__chip-x inline-flex border-0 bg-transparent text-current opacity-70 text-[0.7em] cursor-pointer hover:opacity-100 focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-1 focus-visible:rounded-full"
              :aria-labelledby="`${removeLabelId} ${chipTextId(index)}`"
              @click.stop="removeAt(index)"
            >
              <Icon name="x" :size="10" />
            </button>
          </Badge>
        </li>
      </ul>

      <input
        :id="inputId"
        ref="field"
        v-model="pending"
        class="ui-chip-input__field flex-1 min-w-24 border-0 bg-transparent text-fg outline-none text-[0.8125rem] placeholder:text-muted disabled:cursor-not-allowed"
        type="text"
        :placeholder="model.length ? undefined : placeholder"
        :disabled="disabled"
        :aria-invalid="error || undefined"
        @keydown="onKeydown"
        @paste="onPaste"
        @blur="onBlur"
      />

      <!-- Self-gating: the add control exists only when the caller supplies its localised name,
           so an unnamed icon button cannot ship (contract 04 §9 has no reserved `ui.add`). -->
      <button
        v-if="addLabel && !disabled"
        type="button"
        class="ui-chip-input__add inline-flex items-center justify-center min-w-6 h-6 border border-border rounded-sm bg-surface text-muted text-sm leading-none cursor-pointer enabled:hover:border-ring enabled:hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-1"
        :aria-label="addLabel"
        :disabled="!pending.trim()"
        @click.stop="commitFromButton"
      >
        <Icon name="plus" :size="12" />
      </button>
    </div>

    <span :id="removeLabelId" class="ui-chip-input__sr-only sr-only">{{
      resolvedRemoveLabel
    }}</span>
  </div>
</template>
